import { Button, Icon } from '@openedx/paragon';
import { RiFlashlightLine } from '@remixicon/react';
import { useNavigate } from 'react-router-dom';

import bannerLineSvg from '../../../assets/banner-line.svg';

import './DashboardBanner.scss';

const isAbsoluteUrl = (url) => /^https?:\/\//i.test(url || '');

/**
 * Banner `type` values DashboardBanner can render. The CMS shares a single
 * `type` field across every banner surface, so Discover-only types
 * (`marketing`, `featured`) can show up in the same list — those are not
 * renderable here and must be filtered out before selecting a banner.
 */
export const DASHBOARD_BANNER_TYPES = ['hero', 'split', 'minimal'];

/** Picks the first banner DashboardBanner can actually render, or null. */
export const selectDashboardBanner = (banners) => (
  (banners || []).find((b) => DASHBOARD_BANNER_TYPES.includes(b?.type)) || null
);

const DashboardBanner = ({ banner }) => {
  const navigate = useNavigate();

  if (!banner) return null;

  const {
    type,
    title,
    description,
    badge,
    imageUrl,
    primaryText,
    primaryLink,
    secondaryText,
    secondaryLink,
    variant = 'primary',
  } = banner;

  const handleLink = (url) => {
    if (!url) return;
    if (isAbsoluteUrl(url)) {
      window.location.assign(url);
    } else {
      navigate(url);
    }
  };

  const variantBg = `var(--pgn-color-${variant}-100)`;

  if (type === 'hero') {
    return (
      <div
        className="dashboard-banner dashboard-banner--hero"
        style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : undefined }}
      >
        <div className="dashboard-banner--hero__gradient-left" />
        <div className="dashboard-banner--hero__gradient-bottom" />
        <div className="dashboard-banner--hero__content">
          <div className="dashboard-banner--hero__text-top">
            <div className="dashboard-banner__title-row">
              {title && <h3 className="dashboard-banner--hero__title">{title}</h3>}
              {badge && (
                <div className="dashboard-banner__badge-wrap">
                  <span className="dashboard-banner__badge">
                    <Icon src={RiFlashlightLine} size="xs" />
                    {badge}
                  </span>
                </div>
              )}
            </div>
            {description && <p className="dashboard-banner--hero__description">{description}</p>}
          </div>
          <div className="dashboard-banner--hero__actions">
            {primaryText && (
              <Button
                variant="inverse-primary"
                onClick={() => handleLink(primaryLink)}
              >
                {primaryText}
              </Button>
            )}
            {secondaryText && (
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => handleLink(secondaryLink)}
              >
                {secondaryText}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (type === 'split') {
    return (
      <div
        className="dashboard-banner dashboard-banner--split"
        style={{ backgroundColor: variantBg }}
      >
        <div className="dashboard-banner--split__text">
          <div className="dashboard-banner--split__text-top">
            <div className="dashboard-banner__title-row">
              {title && <h3 className="dashboard-banner--split__title">{title}</h3>}
              {badge && (
                <div className="dashboard-banner__badge-wrap">
                  <span className="dashboard-banner__badge">
                    <Icon src={RiFlashlightLine} size="xs" />
                    {badge}
                  </span>
                </div>
              )}
            </div>
            {description && <p className="dashboard-banner--split__description">{description}</p>}
          </div>
          <div className="dashboard-banner--split__actions">
            {primaryText && (
              <Button
                className="dashboard-banner__solid-btn"
                onClick={() => handleLink(primaryLink)}
              >
                {primaryText}
              </Button>
            )}
            {secondaryText && (
              <Button
                variant="outline-gray"
                size="sm"
                onClick={() => handleLink(secondaryLink)}
              >
                {secondaryText}
              </Button>
            )}
          </div>
        </div>
        <img
          className="dashboard-banner--split__decoration"
          src={bannerLineSvg}
          alt=""
          aria-hidden="true"
        />
        {imageUrl && (
          <img
            className="dashboard-banner--split__image"
            src={imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
          />
        )}
      </div>
    );
  }

  if (type === 'minimal') {
    return (
      <div
        className="dashboard-banner dashboard-banner--minimal"
        style={{ backgroundColor: variantBg }}
      >
        <div className="dashboard-banner--minimal__text">
          {title && <h3 className="dashboard-banner--minimal__title">{title}</h3>}
          {description && <p className="dashboard-banner--minimal__description">{description}</p>}
        </div>
        <img
          className="dashboard-banner--minimal__decoration"
          src={bannerLineSvg}
          alt=""
          aria-hidden="true"
        />
        {primaryText && (
          <Button
            className="dashboard-banner__solid-btn"
            onClick={() => handleLink(primaryLink)}
          >
            {primaryText}
          </Button>
        )}
      </div>
    );
  }

  return null;
};

export default DashboardBanner;
